import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiService} from './api.service';
import {CompanySearchFacets, EmployeeGroup, EnumeratedField, NaceCode} from '../../models/company.model';
import {FilterCriteria} from '../../models/filter.model';
import {HubspotProperty} from '../../models/hubspot.model';

/**
 * Service for retrieving reference data for the Enrich integration
 * This service handles NACE codes, employee groups, HubSpot properties, and Brreg fields
 */
@Injectable({
  providedIn: 'root'
})
export class ReferenceService {
    private apiService = inject(ApiService);

  getSearchFacets(criteria: FilterCriteria, clientId: string): Observable<CompanySearchFacets> {
    return this.apiService.post<CompanySearchFacets>('companies/brreg/search/facets', criteria, {clientId});
  }


  /**
   * Retrieves all unique NACE codes with their descriptions and counts
   * @param clientId The client ID to retrieve NACE codes for
   * @returns An Observable with an array of NACE codes
   */
  getNaceCodes(clientId: string): Observable<NaceCode[]> {
    return this.apiService.get<NaceCode[]>('companies/nace-codes', { clientId });
  }

  /**
   * Retrieves all employee group ranges with counts
   * @param clientId The client ID to retrieve employee groups for
   * @returns An Observable with an array of employee groups
   */
  getEmployeeGroups(clientId: string): Observable<EmployeeGroup[]> {
    return this.apiService.get<EmployeeGroup[]>('companies/employee-groups', { clientId });
  }

  getEnumeratedFieldValues(fieldName: string, clientId: string): Observable<EnumeratedField[]> {
    return this.apiService.get<EnumeratedField[]>(`companies/lookup/${fieldName}`, {clientId});
  }

  /**
   * Retrieves all HubSpot properties for a client
   * @param clientId The client ID to retrieve properties for
   * @returns An Observable with an array of HubSpot properties
   */
  getHubspotProperties(clientId: string): Observable<HubspotProperty[]> {
    return this.apiService.get<HubspotProperty[]>('hubspot/properties', { clientId });
  }

  /**
   * Retrieves all Brreg fields that can be used in syncs and imports
   * @param clientId The client ID to retrieve Brreg fields for
   * @returns An Observable with an array of Brreg field names
   */
  getBrregFields(clientId: string): Observable<string[]> {
    return this.apiService.get<string[]>('hubspot/brreg-fields', { clientId });
  }
}
